import { useEffect } from "react";

const SITE_URL = "https://thermofix.com.ge";

const ensureMetaTag = (selector, attributes) => {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  return tag;
};

const ensureScriptTag = (id, type = "application/ld+json") => {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = type;
    document.head.appendChild(tag);
  }
  return tag;
};

const toAbsoluteUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${SITE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

export default function useSeo({
  title,
  description,
  path = "/",
  keywords = "",
  image = "/boiler.png",
  type = "website",
  noindex = false,
  structuredData = null,
}) {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path}`;
    const imageUrl = toAbsoluteUrl(image);

    document.title = title;

    ensureMetaTag('meta[name="description"]', { name: "description" }).setAttribute(
      "content",
      description
    );

    ensureMetaTag('meta[name="keywords"]', { name: "keywords" }).setAttribute(
      "content",
      keywords
    );
    ensureMetaTag('meta[name="robots"]', { name: "robots" }).setAttribute(
      "content",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    );

    ensureMetaTag('meta[property="og:type"]', { property: "og:type" }).setAttribute(
      "content",
      type
    );
    ensureMetaTag('meta[property="og:site_name"]', { property: "og:site_name" }).setAttribute(
      "content",
      "Thermo Fix"
    );
    ensureMetaTag('meta[property="og:locale"]', { property: "og:locale" }).setAttribute(
      "content",
      "ka_GE"
    );
    ensureMetaTag('meta[property="og:title"]', { property: "og:title" }).setAttribute(
      "content",
      title
    );
    ensureMetaTag('meta[property="og:description"]', { property: "og:description" }).setAttribute(
      "content",
      description
    );
    ensureMetaTag('meta[property="og:url"]', { property: "og:url" }).setAttribute(
      "content",
      canonicalUrl
    );
    ensureMetaTag('meta[property="og:image"]', { property: "og:image" }).setAttribute(
      "content",
      imageUrl
    );
    ensureMetaTag('meta[property="og:image:alt"]', { property: "og:image:alt" }).setAttribute(
      "content",
      title
    );

    ensureMetaTag('meta[name="twitter:card"]', { name: "twitter:card" }).setAttribute(
      "content",
      "summary_large_image"
    );
    ensureMetaTag('meta[name="twitter:title"]', { name: "twitter:title" }).setAttribute(
      "content",
      title
    );
    ensureMetaTag('meta[name="twitter:description"]', { name: "twitter:description" }).setAttribute(
      "content",
      description
    );
    ensureMetaTag('meta[name="twitter:image"]', { name: "twitter:image" }).setAttribute(
      "content",
      imageUrl
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const existingJsonLd = document.getElementById("seo-jsonld");
    if (structuredData) {
      const jsonLd = existingJsonLd || ensureScriptTag("seo-jsonld");
      jsonLd.textContent = JSON.stringify(structuredData);
    } else if (existingJsonLd) {
      existingJsonLd.remove();
    }
  }, [description, image, keywords, noindex, path, structuredData, title, type]);
}
